import { Modal } from '@mui/material';
import React from 'react';
import MapSearch from '../../../../../../../components/MapSearch';
import CreateEditOccurrenceState from '../../../../../domain/usecases/create_edit/CreateEditOccurrenceState';
import { ERequestStatus } from '../../../../../../../shared/enums/requestStatus';
import { EVictimsSituation } from '../../../../../../../shared/enums/victimsSituation';
import { EVictimsTypePerson } from '../../../../../../../shared/enums/victimsTypePerson';
import { IRequestStatus } from '../../../../../../../shared/interface/requestStatus';
import {
  ButtonOutline,
  ButtonPrimary,
  DiffCompareGrid,
  DiffValueAfter,
  DiffValueBefore,
  Footer,
  GroupDetails,
  GroupMeta,
  GroupSummary,
  Item,
  ItemLabel,
  ItemValue,
  ItemWide,
  ItemsGrid,
  LegendRow,
  LegendSwatch,
  LegendText,
  ListCard,
  ListCardHeader,
  ModalContent,
  ModalHeader,
  ModalSubHeaderRow,
  ModalSubtitle,
  ModalTitle,
  MapContainer,
  MapPlaceholder,
  Section,
  SectionTitle,
  SummaryGrid,
  TopSplit,
  TopSplitItem,
  ValueChip,
  ValueList,
  ValuePlaceholder
} from './index.styled';
import { IOccurrenceChangeDiffItem } from '../../../../../../../services/functions/occurrences';

interface IOccurrenceReviewModalProps {
  open: boolean;
  onClose(): void;
  onConfirm?(): void;
  createEditOccurrenceState?: CreateEditOccurrenceState;
  mode?: 'submit' | 'audit';
  auditSnapshot?: Record<string, unknown> | null;
  auditDiff?: IOccurrenceChangeDiffItem[];
  auditChangeType?: string;
  auditCreatedAt?: string;
  auditAuthor?: string;
}

const getRequestList = <T,>(status: IRequestStatus<T[]>): T[] =>
  status.maybeMap({
    succeeded: data => data,
    idle: () => [],
    loading: () => [],
    failed: () => []
  }) || [];

const normalizeValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    return value.filter(
      item => item !== null && item !== undefined && item !== ''
    );
  }

  return value;
};

const hasValueChanged = (currentValue: any, initialValue: any) => {
  const normalizedCurrent = normalizeValue(currentValue);
  const normalizedInitial = normalizeValue(initialValue);

  if (Array.isArray(normalizedCurrent) || Array.isArray(normalizedInitial)) {
    return (
      JSON.stringify(normalizedCurrent || []) !==
      JSON.stringify(normalizedInitial || [])
    );
  }

  return normalizedCurrent !== normalizedInitial;
};

const isFieldDirty = (
  formRef: React.RefObject<any> | undefined,
  field: string
) => {
  if (!formRef?.current) return false;

  const currentValue = formRef.current.values?.[field];
  const initialValue = formRef.current.initialValues?.[field];
  const changed = hasValueChanged(currentValue, initialValue);

  return changed;
};

const OccurrenceReviewModal: React.FC<IOccurrenceReviewModalProps> = ({
  open,
  onClose,
  onConfirm,
  createEditOccurrenceState,
  mode = 'submit',
  auditSnapshot,
  auditDiff = [],
  auditChangeType,
  auditCreatedAt,
  auditAuthor
}) => {
  const isAuditMode = mode === 'audit';

  const formatAuditDateTime = (value?: string) => {
    if (!value) return '---';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleString('pt-BR');
  };

  const stringifyValue = (value: unknown) => {
    if (value === null) return 'null';
    if (value === undefined || value === '') return '---';
    if (typeof value === 'object') return JSON.stringify(value);
    return `${value}`;
  };

  const formatChangeType = (value?: string) => {
    const labels: Record<string, string> = {
      create: 'Criacao',
      update: 'Atualizacao',
      delete: 'Exclusao',
      approve: 'Aprovacao',
      disapprove: 'Reprovacao',
      unapprove: 'Desaprovacao'
    };

    if (!value) return '---';

    return labels[value] || value;
  };

  const flattenSnapshot = (
    input: unknown,
    parentPath = ''
  ): { path: string; value: unknown }[] => {
    if (input === null || input === undefined) {
      return [{ path: parentPath || 'root', value: input }];
    }

    if (Array.isArray(input)) {
      if (input.length === 0) {
        return [{ path: parentPath, value: [] }];
      }

      return input.flatMap((item, index) =>
        flattenSnapshot(item, `${parentPath}[${index}]`)
      );
    }

    if (typeof input === 'object') {
      const entries = Object.entries(input as Record<string, unknown>);

      if (!entries.length) {
        return [{ path: parentPath, value: {} }];
      }

      return entries.flatMap(([key, value]) => {
        const nextPath = parentPath ? `${parentPath}.${key}` : key;

        if (typeof value === 'object' && value !== null) {
          return flattenSnapshot(value, nextPath);
        }

        return [{ path: nextPath, value }];
      });
    }

    return [{ path: parentPath, value: input }];
  };

  if (isAuditMode) {
    const diffPaths = new Set((auditDiff || []).map(item => item.path));
    const flattenedSnapshot = flattenSnapshot(auditSnapshot || {});
    const groupedSnapshot = flattenedSnapshot.reduce((accumulator, item) => {
      const rootKey = item.path.split('.')[0].split('[')[0] || 'root';

      if (!accumulator[rootKey]) {
        accumulator[rootKey] = [];
      }

      accumulator[rootKey].push(item);

      return accumulator;
    }, {} as Record<string, { path: string; value: unknown }[]>);

    return (
      <Modal
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        open={open}
        onClose={onClose}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Revisar alterações da ocorrência</ModalTitle>
            <ModalSubHeaderRow>
              <ModalSubtitle>
                {`Tipo: ${formatChangeType(auditChangeType)} | Autor: ${
                  auditAuthor || 'Sistema'
                } | Data: ${formatAuditDateTime(auditCreatedAt)}`}
              </ModalSubtitle>
              <LegendRow>
                <LegendSwatch />
                <LegendText>Indica que o campo foi alterado</LegendText>
              </LegendRow>
            </ModalSubHeaderRow>
          </ModalHeader>

          <Section>
            <SectionTitle>Diff</SectionTitle>
            {!auditDiff.length ? (
              <ValuePlaceholder>Sem alterações detalhadas.</ValuePlaceholder>
            ) : (
              auditDiff.map((item, index) => (
                <ListCard key={`${item.path}-${index}`}>
                  <ListCardHeader>{item.path}</ListCardHeader>
                  <DiffCompareGrid>
                    <Item $dirty>
                      <ItemLabel>Antes</ItemLabel>
                      <DiffValueBefore>
                        {stringifyValue(item.before)}
                      </DiffValueBefore>
                    </Item>
                    <Item $dirty>
                      <ItemLabel>Depois</ItemLabel>
                      <DiffValueAfter>
                        {stringifyValue(item.after)}
                      </DiffValueAfter>
                    </Item>
                  </DiffCompareGrid>
                </ListCard>
              ))
            )}
          </Section>

          <Section>
            <SectionTitle>Ocorrência completa</SectionTitle>
            {!flattenedSnapshot.length ? (
              <ValuePlaceholder>Snapshot não disponível.</ValuePlaceholder>
            ) : (
              Object.entries(groupedSnapshot).map(([groupName, items]) => {
                const changedCount = items.filter(item =>
                  diffPaths.has(item.path)
                ).length;

                return (
                  <GroupDetails key={groupName} open>
                    <GroupSummary>
                      {groupName}
                      <GroupMeta>
                        {items.length} campos, {changedCount} alterados
                      </GroupMeta>
                    </GroupSummary>
                    <ItemsGrid>
                      {items.map(item => (
                        <Item key={item.path} $dirty={diffPaths.has(item.path)}>
                          <ItemLabel>{item.path}</ItemLabel>
                          <ItemValue>{stringifyValue(item.value)}</ItemValue>
                        </Item>
                      ))}
                    </ItemsGrid>
                  </GroupDetails>
                );
              })
            )}
          </Section>

          <Footer>
            <ButtonOutline onClick={onClose}>Fechar</ButtonOutline>
          </Footer>
        </ModalContent>
      </Modal>
    );
  }

  if (!createEditOccurrenceState || !onConfirm) {
    return null;
  }

  const detailsForm = createEditOccurrenceState.detailsForm;
  const contextInfoForm = createEditOccurrenceState.contextInfoForm;
  const detailsRef = detailsForm.ref;
  const contextRef = contextInfoForm.ref;

  const states = getRequestList(createEditOccurrenceState.statesRequestStatus);
  const cities = getRequestList(createEditOccurrenceState.citiesRequestStatus);
  const neighborhoods = getRequestList(
    createEditOccurrenceState.neighborhoodsRequestStatus
  );
  const subNeighborhoods = getRequestList(
    createEditOccurrenceState.subneighborhoodsRequestStatus
  );
  const localities = getRequestList(
    createEditOccurrenceState.localitiesRequestStatus
  );
  const sources = getRequestList(
    createEditOccurrenceState.sourcesRequestStatus
  );
  const reasons = getRequestList(
    createEditOccurrenceState.reasonsRequestStatus
  );
  const clippings = getRequestList(
    createEditOccurrenceState.clippingsRequestStatus
  );
  const transports = getRequestList(
    createEditOccurrenceState.transportsRequestStatus
  );
  const qualifications = getRequestList(
    createEditOccurrenceState.qualificationsRequestStatus
  );
  const ageGroups = getRequestList(
    createEditOccurrenceState.ageGroupsRequestStatus
  );
  const genres = getRequestList(createEditOccurrenceState.genresRequestStatus);
  const places = getRequestList(createEditOccurrenceState.placesRequestStatus);
  const circumstances = getRequestList(
    createEditOccurrenceState.circumstancesRequestStatus
  );
  const statuses = getRequestList(
    createEditOccurrenceState.statusRequestStatus
  );
  const positions = getRequestList(
    createEditOccurrenceState.positionsRequestStatus
  );
  const coorporations = getRequestList(
    createEditOccurrenceState.coorporationsRequestStatus
  );
  const parties = getRequestList(
    createEditOccurrenceState.partiesRequestStatus
  );
  const animals = getRequestList(
    createEditOccurrenceState.animalsRequestStatus
  );

  const formatValue = (value?: string | number | null) =>
    value === null || value === undefined || value === '' ? '---' : `${value}`;

  const formatBoolean = (value?: boolean) =>
    value === undefined ? '---' : value ? 'Sim' : 'Não';

  const formatDateTime = (value?: string) => {
    if (!value) return '---';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleString('pt-BR');
  };

  const parseCoordinate = (value?: string) => {
    if (value === null || value === undefined) return undefined;
    const trimmed = `${value}`.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const mapLatitude = parseCoordinate(detailsForm.latitude);
  const mapLongitude = parseCoordinate(detailsForm.longitude);
  const mapPosition =
    mapLatitude !== undefined && mapLongitude !== undefined
      ? { lat: mapLatitude, lng: mapLongitude }
      : undefined;

  const labelText = (label: string, required?: boolean) =>
    required ? `${label} *` : label;

  const isRelatedNewsRequired =
    createEditOccurrenceState.fieldsForValidation.pressSource?.id ===
    detailsForm.sourceId;

  const servicesQualifications =
    createEditOccurrenceState.fieldsForValidation.servicesQualifications?.map(
      item => item.id
    ) || [];
  const politicalQualificationId =
    createEditOccurrenceState.fieldsForValidation.politicalQualification?.id ||
    '';
  const agentQualificationId =
    createEditOccurrenceState.fieldsForValidation.agentQualification?.id || '';

  const victimHasQualification = (victim: any, qualificationId: string) => {
    if (!qualificationId) return false;
    const qualifications = Array.isArray(victim.qualifications)
      ? victim.qualifications
      : [];
    return qualifications.includes(qualificationId);
  };

  const isVictimServiceRequired = (victim: any) => {
    const qualifications = Array.isArray(victim.qualifications)
      ? victim.qualifications
      : [];
    return qualifications.some((id: string) =>
      servicesQualifications.includes(id)
    );
  };

  const isVictimPoliticalRequired = (victim: any) =>
    victimHasQualification(victim, politicalQualificationId);

  const isVictimAgentRequired = (victim: any) => {
    const isAgentQualification = victimHasQualification(
      victim,
      agentQualificationId
    );
    const isAgentPerson =
      victim.personType?.toLowerCase() ===
      EVictimsTypePerson.Agent.toLowerCase();
    return isAgentQualification || isAgentPerson;
  };

  const resolveName = (
    id: string | undefined,
    list: { id: string; name?: string }[]
  ) => {
    if (!id) return '---';
    const match = list.find(item => item.id === id);
    return match?.name || '---';
  };

  const resolveNames = (
    ids: string[] | undefined,
    list: { id: string; name?: string }[]
  ) => {
    if (!ids?.length) return [];
    return ids
      .filter(Boolean)
      .map(id => list.find(item => item.id === id)?.name || id);
  };

  const resolveAnimalTypes = (ids: string[] | undefined) => {
    if (!ids?.length) return [];
    return ids
      .filter(Boolean)
      .map(id => animals.find(item => item.id === id)?.type || id);
  };

  const renderValueList = (values: string[], placeholder = '---') => {
    if (!values.length) {
      return <ValuePlaceholder>{placeholder}</ValuePlaceholder>;
    }
    return (
      <ValueList>
        {values.map((value, index) => (
          <ValueChip key={`${value}-${index}`}>{value}</ValueChip>
        ))}
      </ValueList>
    );
  };

  const personTypeLabel = (value?: string) =>
    value === EVictimsTypePerson.Agent
      ? 'Agente'
      : value === EVictimsTypePerson.Civilian
      ? 'Civil'
      : formatValue(value || '');

  const situationLabel = (value?: string) =>
    value === EVictimsSituation.Wounded
      ? 'Ferido'
      : value === EVictimsSituation.Dead
      ? 'Morto'
      : formatValue(value || '');

  const complementaryReasons = Array.isArray(
    contextInfoForm.complementaryReasons
  )
    ? contextInfoForm.complementaryReasons
    : [];
  const clippingIds = Array.isArray(contextInfoForm.clippings)
    ? contextInfoForm.clippings
    : [];

  const isSubmitting =
    createEditOccurrenceState.editOccurrenceRequestStatus.status ===
      ERequestStatus.Loading ||
    createEditOccurrenceState.createOccurrenceRequestStatus.status ===
      ERequestStatus.Loading;

  return (
    <Modal
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      open={open}
      onClose={onClose}
    >
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Revisar ocorrência</ModalTitle>
          <ModalSubHeaderRow>
            <ModalSubtitle>Confira os dados antes de salvar.</ModalSubtitle>
            <LegendRow>
              <LegendSwatch />
              <LegendText>Indica que o campo foi editado</LegendText>
            </LegendRow>
          </ModalSubHeaderRow>
        </ModalHeader>

        <TopSplit>
          <TopSplitItem>
            <Section>
              <SectionTitle>Resumo rápido</SectionTitle>
              <SummaryGrid>
                <Item>
                  <ItemLabel>Total de vítimas</ItemLabel>
                  <ItemValue>
                    {formatValue(createEditOccurrenceState.victimsCount.total)}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Civis feridos</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.victimsCount.civilWounded
                    )}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Civis mortos</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.victimsCount.civilDead
                    )}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Agentes feridos</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.victimsCount.agentWounded
                    )}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Agentes mortos</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.victimsCount.agentDead
                    )}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Total de animais</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.animalVictimsCount.total
                    )}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Animais feridos</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.animalVictimsCount.wounded
                    )}
                  </ItemValue>
                </Item>
                <Item>
                  <ItemLabel>Animais mortos</ItemLabel>
                  <ItemValue>
                    {formatValue(
                      createEditOccurrenceState.animalVictimsCount.dead
                    )}
                  </ItemValue>
                </Item>
              </SummaryGrid>
            </Section>
          </TopSplitItem>
          <TopSplitItem>
            <Section>
              <SectionTitle>Mapa da ocorrência</SectionTitle>
              <MapContainer>
                {mapPosition ? (
                  <MapSearch
                    selectedPosition={mapPosition}
                    height="100%"
                    margin="0"
                    disableInteraction
                    disableGeolocation
                  />
                ) : (
                  <MapPlaceholder>Localização não informada.</MapPlaceholder>
                )}
              </MapContainer>
            </Section>
          </TopSplitItem>
        </TopSplit>

        <Section>
          <SectionTitle>Detalhes da ocorrência</SectionTitle>
          <ItemsGrid>
            <Item $dirty={isFieldDirty(detailsRef, 'documentNumber')}>
              <ItemLabel>Número</ItemLabel>
              <ItemValue>{formatValue(detailsForm.documentNumber)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'date')}>
              <ItemLabel>{labelText('Data e hora', true)}</ItemLabel>
              <ItemValue>{formatDateTime(detailsForm.date)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'address')}>
              <ItemLabel>{labelText('Endereço', true)}</ItemLabel>
              <ItemValue>{formatValue(detailsForm.address)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'stateId')}>
              <ItemLabel>{labelText('Estado', true)}</ItemLabel>
              <ItemValue>{resolveName(detailsForm.stateId, states)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'cityId')}>
              <ItemLabel>{labelText('Cidade', true)}</ItemLabel>
              <ItemValue>{resolveName(detailsForm.cityId, cities)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'neighborhoodId')}>
              <ItemLabel>Bairro</ItemLabel>
              <ItemValue>
                {resolveName(detailsForm.neighborhoodId, neighborhoods)}
              </ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'subNeighborhoodId')}>
              <ItemLabel>Sub-bairro</ItemLabel>
              <ItemValue>
                {resolveName(detailsForm.subNeighborhoodId, subNeighborhoods)}
              </ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'localityId')}>
              <ItemLabel>Localidade</ItemLabel>
              <ItemValue>
                {resolveName(detailsForm.localityId, localities)}
              </ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'latitude')}>
              <ItemLabel>{labelText('Latitude', true)}</ItemLabel>
              <ItemValue>{formatValue(detailsForm.latitude)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'longitude')}>
              <ItemLabel>{labelText('Longitude', true)}</ItemLabel>
              <ItemValue>{formatValue(detailsForm.longitude)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'sourceId')}>
              <ItemLabel>{labelText('Fonte', true)}</ItemLabel>
              <ItemValue>
                {resolveName(detailsForm.sourceId, sources)}
              </ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'complementarySourceId')}>
              <ItemLabel>Fonte complementar</ItemLabel>
              <ItemValue>
                {resolveName(detailsForm.complementarySourceId, sources)}
              </ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'policeAction')}>
              <ItemLabel>{labelText('Ação policial', true)}</ItemLabel>
              <ItemValue>{formatBoolean(detailsForm.policeAction)}</ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(detailsRef, 'agentPresence')}>
              <ItemLabel>{labelText('Presença de agentes', true)}</ItemLabel>
              <ItemValue>{formatBoolean(detailsForm.agentPresence)}</ItemValue>
            </Item>
            <ItemWide $dirty={isFieldDirty(detailsRef, 'relatedRecord')}>
              <ItemLabel>Registro relacionado</ItemLabel>
              <ItemValue>{formatValue(detailsForm.relatedRecord)}</ItemValue>
            </ItemWide>
            <ItemWide $dirty={isFieldDirty(detailsRef, 'description')}>
              <ItemLabel>{labelText('Descrição', true)}</ItemLabel>
              <ItemValue>{formatValue(detailsForm.description)}</ItemValue>
            </ItemWide>
          </ItemsGrid>
        </Section>

        <Section>
          <SectionTitle>Contexto</SectionTitle>
          <ItemsGrid>
            <Item $dirty={isFieldDirty(contextRef, 'mainReasonId')}>
              <ItemLabel>{labelText('Motivo principal', true)}</ItemLabel>
              <ItemValue>
                {resolveName(contextInfoForm.mainReasonId, reasons)}
              </ItemValue>
            </Item>
            <Item $dirty={isFieldDirty(contextRef, 'complementaryReasons')}>
              <ItemLabel>Motivos complementares</ItemLabel>
              {renderValueList(resolveNames(complementaryReasons, reasons))}
            </Item>
            <Item $dirty={isFieldDirty(contextRef, 'clippings')}>
              <ItemLabel>Outros recortes</ItemLabel>
              {renderValueList(resolveNames(clippingIds, clippings))}
            </Item>
            <Item $dirty={isFieldDirty(contextRef, 'massacre')}>
              <ItemLabel>{labelText('Chacina', true)}</ItemLabel>
              <ItemValue>{formatBoolean(contextInfoForm.massacre)}</ItemValue>
            </Item>
            <ItemWide $dirty={isFieldDirty(contextRef, 'policeUnit')}>
              <ItemLabel>Unidades policiais presentes</ItemLabel>
              <ItemValue>{formatValue(contextInfoForm.policeUnit)}</ItemValue>
            </ItemWide>
            <ItemWide $dirty={isFieldDirty(contextRef, 'relatedNews')}>
              <ItemLabel>
                {labelText('Notícias relacionadas', isRelatedNewsRequired)}
              </ItemLabel>
              <ItemValue>{formatValue(contextInfoForm.relatedNews)}</ItemValue>
            </ItemWide>
            <ItemWide $dirty={isFieldDirty(contextRef, 'observations')}>
              <ItemLabel>Observações</ItemLabel>
              <ItemValue>{formatValue(contextInfoForm.observations)}</ItemValue>
            </ItemWide>
          </ItemsGrid>
        </Section>

        <Section>
          <SectionTitle>Transportes</SectionTitle>
          {createEditOccurrenceState.transportsForm.length === 0 ? (
            <ValuePlaceholder>Nenhum transporte informado.</ValuePlaceholder>
          ) : (
            createEditOccurrenceState.transportsForm.map((transport, index) => {
              const transportRef = transport.ref;

              return (
                <ListCard key={transport.uniqueKey}>
                  <ListCardHeader>Transporte {index + 1}</ListCardHeader>
                  <ItemsGrid>
                    <Item $dirty={isFieldDirty(transportRef, 'transportId')}>
                      <ItemLabel>{labelText('Tipo', true)}</ItemLabel>
                      <ItemValue>
                        {resolveName(transport.transportId, transports)}
                      </ItemValue>
                    </Item>
                    <Item
                      $dirty={isFieldDirty(
                        transportRef,
                        'interruptedTransport'
                      )}
                    >
                      <ItemLabel>{labelText('Interrompido', true)}</ItemLabel>
                      <ItemValue>
                        {formatBoolean(transport.interruptedTransport)}
                      </ItemValue>
                    </Item>
                    <Item
                      $dirty={isFieldDirty(transportRef, 'dateInterruption')}
                    >
                      <ItemLabel>Data da interrupção</ItemLabel>
                      <ItemValue>
                        {formatDateTime(transport.dateInterruption)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(transportRef, 'releaseDate')}>
                      <ItemLabel>Data da liberação</ItemLabel>
                      <ItemValue>
                        {formatDateTime(transport.releaseDate)}
                      </ItemValue>
                    </Item>
                    <ItemWide
                      $dirty={isFieldDirty(
                        transportRef,
                        'transportDescription'
                      )}
                    >
                      <ItemLabel>Descrição</ItemLabel>
                      <ItemValue>
                        {formatValue(transport.transportDescription)}
                      </ItemValue>
                    </ItemWide>
                  </ItemsGrid>
                </ListCard>
              );
            })
          )}
        </Section>

        <Section>
          <SectionTitle>Vítimas</SectionTitle>
          {createEditOccurrenceState.victimsForm.length === 0 ? (
            <ValuePlaceholder>Nenhuma vítima informada.</ValuePlaceholder>
          ) : (
            createEditOccurrenceState.victimsForm.map((victim, index) => {
              const qualificationIds = Array.isArray(victim.qualifications)
                ? victim.qualifications
                : [];
              const circumstanceIds = Array.isArray(victim.circumstances)
                ? victim.circumstances
                : [];
              const victimRef = victim.ref;
              return (
                <ListCard key={victim.uniqueKey}>
                  <ListCardHeader>Vítima {index + 1}</ListCardHeader>
                  <ItemsGrid>
                    <Item $dirty={isFieldDirty(victimRef, 'name')}>
                      <ItemLabel>Nome</ItemLabel>
                      <ItemValue>{formatValue(victim.name)}</ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'personType')}>
                      <ItemLabel>{labelText('Tipo', true)}</ItemLabel>
                      <ItemValue>
                        {personTypeLabel(victim.personType)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'situation')}>
                      <ItemLabel>{labelText('Situação', true)}</ItemLabel>
                      <ItemValue>{situationLabel(victim.situation)}</ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'age')}>
                      <ItemLabel>Idade</ItemLabel>
                      <ItemValue>{formatValue(victim.age)}</ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'ageGroupId')}>
                      <ItemLabel>{labelText('Faixa etária', true)}</ItemLabel>
                      <ItemValue>
                        {resolveName(victim.ageGroupId, ageGroups)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'genreId')}>
                      <ItemLabel>{labelText('Gênero', true)}</ItemLabel>
                      <ItemValue>
                        {resolveName(victim.genreId, genres)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'race')}>
                      <ItemLabel>{labelText('Raça', true)}</ItemLabel>
                      <ItemValue>{formatValue(victim.race)}</ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'placeId')}>
                      <ItemLabel>{labelText('Local', true)}</ItemLabel>
                      <ItemValue>
                        {resolveName(victim.placeId, places)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'qualifications')}>
                      <ItemLabel>{labelText('Qualificações', true)}</ItemLabel>
                      {renderValueList(
                        resolveNames(qualificationIds, qualifications)
                      )}
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'circumstances')}>
                      <ItemLabel>{labelText('Circunstâncias', true)}</ItemLabel>
                      {renderValueList(
                        resolveNames(circumstanceIds, circumstances)
                      )}
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'serviceStatusId')}>
                      <ItemLabel>
                        {labelText(
                          'Status de serviço',
                          isVictimServiceRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.serviceStatusId, statuses)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'partieId')}>
                      <ItemLabel>
                        {labelText(
                          'Partido',
                          isVictimPoliticalRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.partieId, parties)}
                      </ItemValue>
                    </Item>
                    <Item
                      $dirty={isFieldDirty(victimRef, 'politicalPositionId')}
                    >
                      <ItemLabel>
                        {labelText(
                          'Cargo político',
                          isVictimPoliticalRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.politicalPositionId, positions)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'politicalStatusId')}>
                      <ItemLabel>
                        {labelText(
                          'Status político',
                          isVictimPoliticalRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.politicalStatusId, statuses)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'coorporationId')}>
                      <ItemLabel>
                        {labelText(
                          'Coorporação',
                          isVictimAgentRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.coorporationId, coorporations)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'agentPositionId')}>
                      <ItemLabel>
                        {labelText(
                          'Posição do agente',
                          isVictimAgentRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.agentPositionId, positions)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'agentStatusId')}>
                      <ItemLabel>
                        {labelText(
                          'Status do agente',
                          isVictimAgentRequired(victim)
                        )}
                      </ItemLabel>
                      <ItemValue>
                        {resolveName(victim.agentStatusId, statuses)}
                      </ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'unit')}>
                      <ItemLabel>Unidade</ItemLabel>
                      <ItemValue>{formatValue(victim.unit)}</ItemValue>
                    </Item>
                    <Item $dirty={isFieldDirty(victimRef, 'deathDate')}>
                      <ItemLabel>Data do óbito</ItemLabel>
                      <ItemValue>{formatDateTime(victim.deathDate)}</ItemValue>
                    </Item>
                    <ItemWide
                      $dirty={isFieldDirty(victimRef, 'generalObservation')}
                    >
                      <ItemLabel>Observações gerais</ItemLabel>
                      <ItemValue>
                        {formatValue(victim.generalObservation)}
                      </ItemValue>
                    </ItemWide>
                  </ItemsGrid>
                </ListCard>
              );
            })
          )}
        </Section>

        <Section>
          <SectionTitle>Vítimas animais</SectionTitle>
          {createEditOccurrenceState.animalVictimsForm.length === 0 ? (
            <ValuePlaceholder>
              Nenhuma vítima animal informada.
            </ValuePlaceholder>
          ) : (
            createEditOccurrenceState.animalVictimsForm.map(
              (animalVictim, index) => {
                const circumstanceIds = Array.isArray(
                  animalVictim.circumstances
                )
                  ? animalVictim.circumstances
                  : [];
                const animalRef = animalVictim.ref;

                return (
                  <ListCard key={animalVictim.uniqueKey}>
                    <ListCardHeader>Animal {index + 1}</ListCardHeader>
                    <ItemsGrid>
                      <Item $dirty={isFieldDirty(animalRef, 'animalTypeId')}>
                        <ItemLabel>{labelText('Tipo', true)}</ItemLabel>
                        <ItemValue>
                          {resolveAnimalTypes([animalVictim.animalTypeId])[0] ||
                            '---'}
                        </ItemValue>
                      </Item>
                      <Item $dirty={isFieldDirty(animalRef, 'situation')}>
                        <ItemLabel>{labelText('Situação', true)}</ItemLabel>
                        <ItemValue>
                          {situationLabel(animalVictim.situation)}
                        </ItemValue>
                      </Item>
                      <Item $dirty={isFieldDirty(animalRef, 'name')}>
                        <ItemLabel>Nome</ItemLabel>
                        <ItemValue>{formatValue(animalVictim.name)}</ItemValue>
                      </Item>
                      <Item $dirty={isFieldDirty(animalRef, 'circumstances')}>
                        <ItemLabel>
                          {labelText('Circunstâncias', true)}
                        </ItemLabel>
                        {renderValueList(
                          resolveNames(circumstanceIds, circumstances)
                        )}
                      </Item>
                      <Item $dirty={isFieldDirty(animalRef, 'deathDate')}>
                        <ItemLabel>Data do óbito</ItemLabel>
                        <ItemValue>
                          {formatDateTime(animalVictim.deathDate)}
                        </ItemValue>
                      </Item>
                    </ItemsGrid>
                  </ListCard>
                );
              }
            )
          )}
        </Section>

        <Footer>
          <ButtonOutline onClick={onClose}>Voltar e editar</ButtonOutline>
          <ButtonPrimary onClick={onConfirm} disabled={isSubmitting}>
            Confirmar envio
          </ButtonPrimary>
        </Footer>
      </ModalContent>
    </Modal>
  );
};

export default OccurrenceReviewModal;
